import React from 'react';
import propTypes from 'prop-types';
import { sampleSize } from 'lodash';

import { getMessage } from 'util/locale';

import { addPrefixToElementIds, mightContainHtmlTag } from 'util/html';

import { logError, treatErrorAs } from 'api/errors';
import { errorLevel } from 'constants/errors';

const _generateIdPrefix = () => sampleSize('abcdefghijklmnopqrstuvwxyz0123456789', 6).join('') + '-';

/* Component for safely rendering Il8n messages that contain HTML. In theory, this is the only place in the project
   where "dangerouslySetInnerHTML" needs to be used.

   Using tracked elements:
   If there is an interactive element in the middle of the text, e.g. a link that opens a popup, you can get
   a reference to the element for use in setting up popups or other interaction. To do this: The message text in
   the resource file should include an element with an ID attribute defined,
   e.g. `my-interactive-link: "This is <a id='linkId'>a link</a>"`. In rendering, the ID will be instrumented
   to keep it unique inside of the rendering. So the ID attribute you choose can be short without worrying
   about namespace conflicts. Pass appropriate values to trackedElementIds and onTrackedElementsMounted to receive
   the DOM element in a callback.

   Anti-pattern: DON'T add a property that passes the message text itself. By requiring use of a message ID to look up
   a resource, you can be reasonably sure that the HTML-containing text is coming from a trusted, injection-free
   source.

   Anti-pattern: DON'T sanitize the params inside this class so that they can safely contain HTML, (e.g. escaping
   < to &lt;). The design is that HTML will be found only in the trusted content of the localized message content
   loaded via i18n. We intentionally fail and log an error when any HTML is found in params, because this may warn
   us of an attack or at least an attack vector. You can however, escape special characters in params BEFORE they are
   passed via messageParams prop to HtmlText--just avoid doing it in a generalized way that applies to unknown input.
*/
class HtmlText extends React.Component {
  constructor(props) {
    super(props);
    this.state = { idPrefix: _generateIdPrefix() };
  }

  componentDidMount() {
    // Call back with elements matching tracked element IDs.
    if (this.props.trackedElementIds && this.props.onTrackedElementsMounted) {
      const trackedElements = {};
      this.props.trackedElementIds.forEach(trackedElementId => {
        const element = document.getElementById(this.state.idPrefix + trackedElementId);
        if (!element) {
          // Hint: Check for the resource text having an ID different than what was passed via elementId.
          logError(`Element ID '${trackedElementId}' not found in document.`);
          return;
        }
        trackedElements[trackedElementId] = element;
      });
      this.props.onTrackedElementsMounted(trackedElements);
    }
  }

  render() {
    let safeHtml;
    const ACCEPTED_PARAM_TYPES = ['string', 'number', 'boolean'];

    if (this.props.messageParams) {
      // Params may be data from a web service or other facility vulnerable to attacks. So no HTML tags allowed
      // in params.
      try {
        Object.values(this.props.messageParams).forEach((param) => {
          if (ACCEPTED_PARAM_TYPES.indexOf(typeof param) === -1) {
            throw new Error(
              `Param "${param}" for ${this.props.messageKey} message was unaccepted type ${typeof param}.`); // Avoid any subtleties of converting params to a string.
          }
          if (mightContainHtmlTag(String(param))) { // Some non-display variables will get evaluated, but that's okay--they don't need to contain HTML either.
            throw new Error(
              `Param "${param}" for ${this.props.messageKey} message should not contain HTML characters!`);
          }
        });
      } catch (err) {
        treatErrorAs({ severity: errorLevel.WARN })(err);
        return null; // Intentionally return null to avoid rendering any dangerous data. It is a non-goal to sanitize the HTML for display--see anti-pattern comments above.
      }
      safeHtml = HtmlText.getMessage(this.props.messageKey, {...this.props.messageParams});
    } else {
      safeHtml = HtmlText.getMessage(this.props.messageKey);
    }

    // If tracking elements, then prefix any "id" attribute values with a random namespace text.
    if (this.props.trackedElementIds && this.props.onTrackedElementsMounted) {
      safeHtml = addPrefixToElementIds(safeHtml, this.state.idPrefix);
    }

    return (
      <span className={this.props.className} dangerouslySetInnerHTML={{__html: safeHtml }} /> // eslint-disable-line react/no-danger
    );
  }
}

// A workaround to allow spying on i18n functions in unit tests.
HtmlText.getMessage = getMessage;

HtmlText.propTypes = {
  className:      propTypes.string,
  messageKey:     propTypes.string.isRequired, // ID of text to load from i18n (trusted resource)
  messageParams:  propTypes.object, // Object containing members with names matching the insertion
  // variables within the i18n text, e.g. "Yield: {yieldValue}". These
  // member values should be strings and not contain HTML. If they do
  // contain HTML (or characters that could be used to form HTML tags),
  // component won't render and an error will fire.
  onTrackedElementsMounted: propTypes.func, // Optional callback to receive object containing key/value pairs of
  // tracked elements. Keys are element IDs and values are HTML elements.
  trackedElementIds: propTypes.arrayOf(propTypes.string) // Optional array of id attributes of HTML element appearing in rendered text.
};

export default HtmlText;
